import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Container, Button, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent, Alert, TextField } from '@mui/material';

import { ITransporterExportRequest, ITransporterLight } from "../../../types/transporter";

import { AppConfig } from "../../../config/appConfig";
import { useUserContext } from "../../../contexts/userContext";

import { TransporterService } from "../../../services/transporterService";
import { UtilityService } from "../../../services/utilityService";

import Navigation from "../../header";
import PermissionGate from "../../permissionGate";
import Spinner from "../../spinner";
import TransporterTable from "../../tables/transporterTable";
import WorkingModal, { IWorkingModalProps } from "../../workingModal";

import "../../../styles/entity/transporterList.scss";

const TransporterList = () => {
	const userContext = useUserContext();
	const [gridView, setGridView] = useState<ITransporterLight[]>();

	//Filtering
	const [nameOptions, setNameOptions] = useState<string[]>([]);
	const [stateOptions, setStateOptions] = useState<string[]>([]);
	const [cityOptions, setCityOptions] = useState<string[]>([]);
	const [statusOptions, setStatusOptions] = useState<string[]>([]);
	const [nameFilter, setNameFilter] = useState("");
	const [cityFilter, setCityFilter] = useState("All");
	const [stateFilter, setStateFilter] = useState("All");
	const [statusFilter, setStatusFilter] = useState("Active");
	const [filteredResultCount, setFilteredResultCount] = useState(0);

	const [isLoadingTransporters, setIsLoadingTransporters] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);
	const [exportSortField, setExportSortField] = useState("");
	const [exportSortAscending, setExportSortAscending] = useState(true);

	const loadTransporterData = async () => {
		setIsLoadingTransporters(true);
		setErrorMessage(undefined);

		TransporterService.getTransporters()
			.then((response) => {
				console.log('TransporterService.getTransporters: SUCCESS', response);
				let names: string[] = [];
				let cities: string[] = [];
				let states: string[] = [];
				let statuses: string[] = [];

				let transporters = TransporterService.mapTransporterLightList(response.data);

				transporters.map((item) => {
					if (item.name && !names.includes(item.name)) {
						names.push(item.name);
					}

					if (item.city && !cities.includes(item.city)) {
						cities.push(item.city);
					}

					if (item.state && !states.includes(item.state)) {
						states.push(item.state);
					}

					if (item.status && !statuses.includes(item.status)) {
						statuses.push(item.status);
					}
				});

				setGridView(transporters);
				setNameOptions(names.sort());
				setCityOptions(cities.sort());
				setStateOptions(states.sort());
				setStatusOptions(statuses.sort());
			})
			.catch((error) => {
				console.log('TransporterService.getTransporters: ERROR', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
			})
			.finally(() => {
				setIsLoadingTransporters(false);
			});
	}

	useEffect(() => {
		loadTransporterData();
	}, [])

	const handleSelectState = (event: SelectChangeEvent) => {
		setStateFilter(event.target.value);
	};

	const handleSelectCity = (event: SelectChangeEvent) => {
		setCityFilter(event.target.value);
	};

	const handleSelectStatus = (event: SelectChangeEvent) => {
		setStatusFilter(event.target.value);
	};

	const handleFilteredResultsChanged = (results: ITransporterLight[]) => {
		setFilteredResultCount(results?.length ?? 0);
	};

	const handleSortOptionsChanged = (sortField: string, sortAscending: boolean) => {
		setExportSortField(sortField);
		setExportSortAscending(sortAscending);
	};

	const handleExportTransporters = () => {
		setWorkingModalParams({ params: { content: "Exporting transporters..." } });

		var request: ITransporterExportRequest = {
			name: nameFilter?.trim() ? nameFilter?.trim() : undefined,
			state: stateFilter == "All" ? undefined : stateFilter,
			city: cityFilter == "All" ? undefined : cityFilter,
			status: statusFilter == "All" ? undefined : statusFilter.toLowerCase() == "active",
			sortField: exportSortField,
			sortAscending: exportSortAscending
		};

		TransporterService.exportTransporterListToExcel(request)
			.then((response) => {
				console.log('TransporterService.exportTransporterListToExcel: SUCCESS', response);
				UtilityService.downloadGeneratedDocument(response.data);
			})
			.catch((error) => {
				console.log('TransporterService.exportTransporterListToExcel: ERROR', error);
			})
			.finally(() => {
				setWorkingModalParams(null);
			});
	};

	return (
		<div className="hwi-transporter-list">
			<Navigation></Navigation>
			<Container maxWidth="lg" className="route-container">
				<PermissionGate permission={userContext.permissions?.canManageTransporters}>
					<section className="hwi-entity-search-bar">
						<div className="search-header">
							<div className="title">
								Search Transporters
							</div>
							<div className="actions">
								{userContext.permissions?.canExportTransporters && <Button variant="contained" color="primary" disabled={isLoadingTransporters || !filteredResultCount} onClick={handleExportTransporters}>Export Transporters</Button>}
								<NavLink to="/transporter/add"><Button variant="outlined">New Transporter</Button></NavLink>
							</div>
						</div>
						<section className="search-options">
							<FormControl size="small">
								<TextField
									label="Name"
									name="name"
									value={nameFilter}
									size="small"
									autoComplete="off"
									onChange={(event) => { setNameFilter(event.target.value) }}
								></TextField>
							</FormControl>
							<FormControl size="small">
								<InputLabel>State</InputLabel>
								<Select
									value={stateFilter}
									label="State"
									onChange={handleSelectState}
								>
									<MenuItem value="All" key="-1">All</MenuItem>
									{stateOptions.map((item, index) => (
										<MenuItem value={item} key={index}>{item}</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl size="small">
								<InputLabel>City</InputLabel>
								<Select
									value={cityFilter}
									label="City"
									onChange={handleSelectCity}
								>
									<MenuItem value="All" key="-1">All</MenuItem>
									{cityOptions.map((item, index) => (
										<MenuItem value={item} key={index}>{item}</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl size="small">
								<InputLabel>Status</InputLabel>
								<Select
									value={statusFilter}
									label="Status"
									onChange={handleSelectStatus}
								>
									<MenuItem value="All" key="-1">All</MenuItem>
									{statusOptions.map((item, index) => (
										<MenuItem value={item} key={index}>{item}</MenuItem>
									))}
								</Select>
							</FormControl>
						</section>
					</section>
					<section className="results">
						<Spinner dependency={isLoadingTransporters} text="Loading Transporters...">
							{errorMessage &&
								<Alert className="alert-error"
									severity="error">
									{errorMessage}
								</Alert>
							}
							{!errorMessage &&
								<TransporterTable
									data={gridView}
									name={nameFilter}
									state={stateFilter}
									city={cityFilter}
									status={statusFilter}
									onFilteredResultsChanged={handleFilteredResultsChanged}
									onSortOptionschanged={handleSortOptionsChanged}
								></TransporterTable>
							}
						</Spinner>
					</section>
				</PermissionGate>
				<WorkingModal params={workingModalParams?.params}></WorkingModal>
			</Container>
		</div>
	);
};

export default TransporterList;